import React, { useState, useEffect } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { eventsAPI } from '../../lib/api';
import { CalendarBlank, MapPin, Clock, CalendarX } from '@phosphor-icons/react';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await eventsAPI.getPublic();
        setEvents(response.data.sort((a, b) => new Date(a.date) - new Date(b.date)));
      } catch (error) {
        console.log('Error fetching events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date());
  const pastEvents = events.filter(e => new Date(e.date) < new Date());

  return (
    <PublicLayout>
      {/* Header */}
      <section 
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_chivalric-office/artifacts/2hebjf1p_e3acf92b-bcec-4e20-9c0b-79317d0fc1bf.jpeg)'
        }}
        data-testid="events-header"
      >
        <div className="absolute inset-0 bg-[#1A1614]/70" />
        <div className="relative z-10 text-center">
          <p className="text-overline mb-4">Gatherings & Ceremonies</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
            Events
          </h1>
          <p className="text-[#F5F2EB]/80 max-w-2xl mx-auto px-4">
            Investitures, ceremonies, and gatherings of the Grand Priory
          </p>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 bg-white" data-testid="upcoming-events">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-8">Upcoming Events</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="space-y-6">
              {upcomingEvents.map(event => (
                <div 
                  key={event.id}
                  className="bg-[#F5F2EB] border border-[#D8D2C4] p-6 flex gap-6"
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="w-20 h-20 bg-[#7B1818] text-white flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-heading">
                      {new Date(event.date).getDate()}
                    </span>
                    <span className="text-xs uppercase">
                      {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-overline text-xs mb-1">{event.event_type?.toUpperCase() || 'EVENT'}</p>
                    <h3 className="font-heading text-xl text-[#1A1614] mb-2">{event.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-[#4A453E]">
                      {event.time && (
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {event.time}
                        </span>
                      )}
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} /> {event.location}
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-[#4A453E] mt-3">{event.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#F5F2EB] border border-[#D8D2C4]">
              <CalendarX size={48} className="mx-auto text-[#8A847A] mb-4" />
              <p className="text-[#8A847A]">No upcoming events scheduled at this time.</p>
            </div>
          )}
        </div>
      </section>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <section className="py-16 bg-[#F5F2EB]" data-testid="past-events">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="font-heading text-3xl text-[#1A1614] mb-8">Past Events</h2>
            <div className="space-y-4">
              {pastEvents.slice(0, 5).map(event => (
                <div 
                  key={event.id}
                  className="bg-white border border-[#D8D2C4] p-4 flex items-center gap-4"
                >
                  <CalendarBlank size={20} className="text-[#8A847A]" />
                  <div>
                    <h3 className="font-medium text-[#1A1614]">{event.title}</h3>
                    <p className="text-sm text-[#8A847A]">
                      {new Date(event.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
};

export default EventsPage;
